import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, AlertTriangle, Calendar, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

interface FollowUpCounts {
  due_count: number;
  overdue_count: number;
  today_count: number;
}

interface FollowUpWidgetProps {
  tenantId: string;
}

export const FollowUpWidget = ({ tenantId }: FollowUpWidgetProps) => {
  const { user } = useAuth();
  const [counts, setCounts] = useState<FollowUpCounts>({
    due_count: 0,
    overdue_count: 0,
    today_count: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) {
      fetchFollowUpCounts();
    }
  }, [tenantId]);

  const fetchFollowUpCounts = async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .rpc('get_follow_up_counts', {
          target_tenant_id: tenantId
        });

      if (error) throw error;
      
      if (data && data.length > 0) {
        setCounts(data[0]);
      }
    } catch (error) {
      console.error('Error fetching follow-up counts:', error);
      toast.error('Fehler beim Laden der Wiedervorlagen-Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const checkFollowUpsNow = async () => {
    try {
      toast.loading('Überprüfe Wiedervorlagen...', { id: 'check-follow-ups' });
      
      const { error } = await supabase.functions.invoke('check-follow-ups', {
        body: { source: 'manual', trigger_user: user?.id }
      });

      if (error) throw error;
      
      toast.success('Wiedervorlagen-Überprüfung abgeschlossen', { id: 'check-follow-ups' });
      fetchFollowUpCounts(); // Refresh counts
    } catch (error) {
      console.error('Error checking follow-ups:', error);
      toast.error('Fehler bei der Wiedervorlagen-Überprüfung', { id: 'check-follow-ups' });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-1/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasFollowUps = counts.due_count > 0;
  const hasOverdue = counts.overdue_count > 0;

  return (
    <Card className={hasOverdue ? 'border-destructive/50 bg-destructive/5' : hasFollowUps ? 'border-warning/50 bg-warning/5' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className={`h-5 w-5 ${hasOverdue ? 'text-destructive' : hasFollowUps ? 'text-warning' : 'text-muted-foreground'}`} />
            <CardTitle className="text-lg">CRM Wiedervorlagen</CardTitle>
          </div>
          {hasFollowUps && (
            <Badge variant={hasOverdue ? 'destructive' : 'secondary'} className="ml-2">
              {counts.due_count}
            </Badge>
          )}
        </div>
        <CardDescription>
          Fällige Aufgaben in Ihrem Empfehlungs-CRM
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3">
          {hasOverdue && (
            <div className="flex items-center justify-between p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <span className="font-medium text-destructive">Überfällig</span>
              </div>
              <Badge variant="destructive">{counts.overdue_count}</Badge>
            </div>
          )}
          
          {counts.today_count > 0 && (
            <div className="flex items-center justify-between p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="font-medium">Heute fällig</span>
              </div>
              <Badge variant="secondary">{counts.today_count}</Badge>
            </div>
          )}

          {counts.due_count === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Keine fälligen Wiedervorlagen</p>
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-2">
          <Button asChild variant="outline" className="flex-1">
            <Link to="/admin/referral-crm" className="flex items-center gap-2">
              CRM öffnen
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
          
          {hasFollowUps && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={checkFollowUpsNow}
              className="shrink-0"
            >
              <Clock className="h-4 w-4" />
            </Button>
          )}
        </div>

        {hasFollowUps && (
          <div className="text-xs text-muted-foreground pt-2 border-t">
            💡 Sie erhalten automatisch tägliche E-Mail-Erinnerungen für fällige Wiedervorlagen.
          </div>
        )}
      </CardContent>
    </Card>
  );
};